import React, { useEffect, useState, useRef } from 'react';
import { Typography, Button, Dropdown } from 'antd';
import { GlobalOutlined, DownOutlined } from '@ant-design/icons';
import { languages } from '@/utils/translation';

const { Text } = Typography;

interface LanguageSelectorProps {
	showLabel?: boolean;
	showInfo?: boolean;
	className?: string;
	isResellerDashboard?: boolean;
	onLanguageChange?: (language: string) => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
	showLabel = true,
	showInfo = true,
	className = '',
	isResellerDashboard = false,
	onLanguageChange,
}) => {
	const [currentLanguage, setCurrentLanguage] = useState<string>('en');
	const isInitialMount = useRef(true);
	const selectedLanguage = useRef<string>('en');
	const ignoreNextUpdate = useRef(false);

	// convert languages object to array of { code, name } objects
	const languageOptions = Object.entries(languages).map(([code, name]) => ({
		code,
		name,
	}));

	const handleLanguageChange = (value: string) => {
		ignoreNextUpdate.current = true;
		selectedLanguage.current = value;
		setCurrentLanguage(value);

		if (onLanguageChange) {
			onLanguageChange(value);
		}
	};

	const getCurrentLanguage = (): string => {
		try {
			const match = document.cookie.match(/(^|;)\s*googtrans=([^;]+)/);
			if (match) {
				const langPair = decodeURIComponent(match[2]);
				const targetLang = langPair.split('/')[2];
				if (targetLang && targetLang !== 'en') {
					return targetLang;
				}
			}

			const htmlElement = document.querySelector('html');
			if (htmlElement && htmlElement.lang && htmlElement.lang !== 'en') {
				return htmlElement.lang;
			}
		} catch (error) {
			console.error('Error getting current language:', error);
		}

		return selectedLanguage.current;
	};

	useEffect(() => {
		if (isInitialMount.current) {
			const initialLang = getCurrentLanguage();
			selectedLanguage.current = initialLang;
			setCurrentLanguage(initialLang);
			if (onLanguageChange) {
				onLanguageChange(initialLang);
			}
			isInitialMount.current = false;
		}

		const observer = new MutationObserver((mutations) => {
			if (ignoreNextUpdate.current) {
				return;
			}

			mutations.forEach((mutation) => {
				if (mutation.type === 'attributes' && mutation.attributeName === 'lang') {
					const htmlElement = document.querySelector('html');
					if (htmlElement && htmlElement.lang) {
						const newLang = htmlElement.lang;
						if (newLang !== currentLanguage) {
							selectedLanguage.current = newLang;
							setCurrentLanguage(newLang);
							if (onLanguageChange) {
								onLanguageChange(newLang);
							}
						}
					}
				}
			});
		});

		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['lang'],
		});

		// Only check for cookie changes, not for selected language
		const interval = setInterval(() => {
			if (ignoreNextUpdate.current) {
				return;
			}

			const cookieLang = getCurrentLanguage();
			if (cookieLang !== currentLanguage && cookieLang !== selectedLanguage.current) {
				setCurrentLanguage(cookieLang);
				selectedLanguage.current = cookieLang;
				if (onLanguageChange) {
					onLanguageChange(cookieLang);
				}
			}
		}, 2000);

		return () => {
			observer.disconnect();
			clearInterval(interval);
		};
	}, [currentLanguage, onLanguageChange]);

	const getLanguageName = (code: string): string => {
		return languages[code] || 'English';
	};

	const items = languageOptions.map((lang) => ({
		key: lang.code,
		label: (
			<div
				className="language-item"
				onClick={() => handleLanguageChange(lang.code)}
				style={{
					padding: '8px 12px',
					cursor: 'pointer',
					backgroundColor: currentLanguage === lang.code ? '#f0f0f0' : 'transparent',
				}}
			>
				{lang.name}
			</div>
		),
	}));

	return (
		<div className={`language-selector ${className}`}>
			<div className="flex items-center">
				{showLabel && (
					<div className="mr-3">
						<Text type="secondary" className="block text-sm mb-0">
							Display Language
						</Text>
					</div>
				)}

				<div className="w-full overflow-hidden">
					<Dropdown
						menu={{
							items,
							style: {
								maxHeight: '400px',
								overflowY: 'auto',
							},
						}}
						trigger={['click']}
						placement="bottom"
						align={{ offset: [0, 8] }}
						disabled={isResellerDashboard}
					>
						<Button
							className="language-dropdown-button w-full flex justify-between items-center"
							style={{
								height: '40px',
								padding: '0 15px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								borderRadius: '6px',
								border: '1px solid #d9d9d9',
								backgroundColor: '#fff',
							}}
						>
							<div className="flex items-center">
								<GlobalOutlined style={{ marginRight: '8px' }} />
								<span>{getLanguageName(currentLanguage)}</span>
							</div>
							<DownOutlined />
						</Button>
					</Dropdown>
				</div>
			</div>

			{showInfo && (
				<div className="mt-2">
					<Text type="secondary" className="text-xs">
						<GlobalOutlined className="mr-1" />
						Changing the language will translate the interface
					</Text>
				</div>
			)}
		</div>
	);
};

export default LanguageSelector;
